import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare,
  FileText,
  ClipboardList,
  Shield,
  CreditCard,
  HelpCircle,
  ChevronRight,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Info,
  ArrowRight,
  Phone,
  Mail,
  User,
  Hash,
  Tag,
  MessageCircle,
  Lightbulb,
  ExternalLink,
  BookOpen
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PortalLayout from '../components/PortalLayout';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick Action Cards Configuration
const quickActions = [
  {
    id: 'file-claim',
    title: 'File a Claim',
    description: 'Start a new diminished value claim',
    icon: FileText,
    path: '/claims/file',
    color: 'green',
    priority: true
  },
  {
    id: 'track-claim',
    title: 'Track a Claim',
    description: 'Check status of existing claims',
    icon: ClipboardList,
    path: '/claims',
    color: 'blue'
  },
  {
    id: 'coverage',
    title: 'View Coverage Details',
    description: 'Review your policy coverage',
    icon: Shield,
    path: '/coverage',
    color: 'purple'
  },
  {
    id: 'billing',
    title: 'Billing & Payments',
    description: 'Manage payment methods and history',
    icon: CreditCard,
    path: '/settings?tab=billing',
    color: 'orange'
  },
  {
    id: 'help',
    title: 'Help Center / FAQ',
    description: 'Find answers to common questions',
    icon: HelpCircle,
    path: '/help',
    color: 'teal'
  }
];

// Color mappings
const colorMap = {
  green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', iconBg: 'bg-green-100', hoverBg: 'hover:bg-green-100' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', iconBg: 'bg-blue-100', hoverBg: 'hover:bg-blue-100' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', iconBg: 'bg-purple-100', hoverBg: 'hover:bg-purple-100' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', iconBg: 'bg-orange-100', hoverBg: 'hover:bg-orange-100' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', iconBg: 'bg-teal-100', hoverBg: 'hover:bg-teal-100' }
};

// Inquiry types with routing suggestions
const inquiryTypes = [
  { value: '', label: 'Select a topic...' },
  { value: 'claims', label: 'Claims', suggestion: 'claims' },
  { value: 'billing', label: 'Billing', suggestion: 'billing' },
  { value: 'coverage', label: 'Coverage Questions', suggestion: 'coverage' },
  { value: 'account', label: 'Account Access', suggestion: null },
  { value: 'cancellation', label: 'Cancellation', suggestion: 'cancellation' },
  { value: 'complaint', label: 'File a Complaint', suggestion: 'complaint' },
  { value: 'other', label: 'Other', suggestion: null }
];

// Conditional suggestions based on inquiry type
const inquirySuggestions = {
  claims: {
    title: 'Before you submit...',
    message: 'Have you already filed a claim? Most claim questions can be resolved through our Claims Center.',
    actions: [
      { label: 'File a New Claim', path: '/claims/file', primary: true },
      { label: 'Track Existing Claim', path: '/claims', primary: false }
    ]
  },
  billing: {
    title: 'Manage your billing online',
    message: 'You can view payment history, update your payment method, and see upcoming charges in your billing settings.',
    actions: [
      { label: 'Go to Billing', path: '/settings?tab=billing', primary: true }
    ]
  },
  coverage: {
    title: 'Review your coverage',
    message: 'You can view your coverage details, limits, deductible, and policy documents online.',
    actions: [
      { label: 'View Coverage Details', path: '/coverage', primary: true },
      { label: 'View Documents', path: '/portal', primary: false }
    ]
  },
  cancellation: {
    title: 'Cancellation information',
    message: 'Before cancelling, please review our refund policy and cancellation terms in the Help Center.',
    actions: [
      { label: 'View Cancellation Policy', path: '/help?category=cancellation', primary: true },
      { label: 'Request Cancellation', path: '/settings?tab=policy', primary: false }
    ]
  },
  complaint: {
    title: 'Filing a Complaint',
    message: 'We take complaints seriously. You can file directly with us or contact your state insurance department. Filing a complaint will NOT affect your policy or claims.',
    actions: [
      { label: 'View Complaint Procedure', path: '/help?category=complaints', primary: true },
      { label: 'View Your Complaint Notice', path: '/portal/documents', primary: false }
    ]
  }
};

// Related FAQ articles based on inquiry type
const relatedArticles = {
  claims: [
    { id: 'how-to-file-claim', question: 'How do I file a claim?', category: 'claims' },
    { id: 'required-documents', question: 'What documents are required for a claim?', category: 'claims' },
    { id: 'claim-deadline', question: 'What is the deadline to file a claim?', category: 'claims' },
    { id: 'claim-timeline', question: 'How long does claim review take?', category: 'claims' }
  ],
  billing: [
    { id: 'how-billing-works', question: 'How does billing work?', category: 'billing' },
    { id: 'failed-payment', question: 'What happens if my payment fails?', category: 'billing' },
    { id: 'update-payment', question: 'How do I update my payment method?', category: 'billing' },
    { id: 'refund-policy', question: 'What is your refund policy?', category: 'billing' }
  ],
  coverage: [
    { id: 'what-does-losspay-cover', question: 'What does LossPay cover?', category: 'coverage' },
    { id: 'what-is-diminished-value', question: 'What is diminished value?', category: 'coverage' },
    { id: 'what-is-not-covered', question: 'What is NOT covered?', category: 'coverage' },
    { id: 'coverage-limits', question: 'What are coverage limits and deductibles?', category: 'coverage' }
  ],
  account: [
    { id: 'create-account', question: 'How do I create or access my account?', category: 'account' },
    { id: 'reset-password', question: 'How do I reset my password?', category: 'account' },
    { id: 'update-info', question: 'How do I update my personal information?', category: 'account' },
    { id: 'security-tips', question: 'What security best practices should I follow?', category: 'account' }
  ],
  cancellation: [
    { id: 'how-to-cancel', question: 'How do I cancel my policy?', category: 'cancellation' },
    { id: 'cancellation-effective', question: 'When does cancellation become effective?', category: 'cancellation' },
    { id: 'refund-eligibility', question: 'Am I eligible for a refund if I cancel?', category: 'cancellation' },
    { id: 're-enrollment', question: 'Can I re-enroll after cancelling?', category: 'cancellation' }
  ],
  complaint: [
    { id: 'how-to-file-complaint', question: 'How do I file a complaint?', category: 'complaints' },
    { id: 'policyholder-rights', question: 'What are my rights as a policyholder?', category: 'complaints' },
    { id: 'state-regulator-contact', question: 'How do I contact my state insurance department?', category: 'complaints' },
    { id: 'complaint-wont-affect-policy', question: 'Will filing a complaint affect my policy?', category: 'complaints' }
  ],
  other: [
    { id: 'what-does-losspay-cover', question: 'What does LossPay cover?', category: 'coverage' },
    { id: 'how-to-file-claim', question: 'How do I file a claim?', category: 'claims' },
    { id: 'when-contact', question: 'When should I contact support?', category: 'support' },
    { id: 'response-times', question: 'What are your response times?', category: 'support' }
  ]
};

export default function ContactSupportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    policyNumber: '',
    inquiryType: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [ticketNumber, setTicketNumber] = useState(null);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const [userPolicyNumber, setUserPolicyNumber] = useState(null);

  // Auto-fill user data
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const policyNum = data.policy?.policy_number || data.primary_policy?.policy_number || '';
          setUserPolicyNumber(policyNum);
          setFormData(prev => ({
            ...prev,
            fullName: data.user?.full_name || `${data.user?.first_name || ''} ${data.user?.last_name || ''}`.trim(),
            email: data.user?.email || '',
            policyNumber: policyNum
          }));
        }
      } catch (err) {
        console.log('[ContactSupport] Could not fetch user data:', err);
      }
    };

    fetchUserData();
  }, []);

  // Handle inquiry type change with suggestion
  const handleInquiryChange = (value) => {
    setFormData(prev => ({ ...prev, inquiryType: value }));
    const type = inquiryTypes.find(t => t.value === value);
    if (type?.suggestion && inquirySuggestions[type.suggestion] && !dismissedSuggestion) {
      setShowSuggestion(true);
    } else {
      setShowSuggestion(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/support/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          ...formData,
          submitted_at: new Date().toISOString(),
          user_agent: navigator.userAgent
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        setTicketNumber(data.ticket_number);
        window.scrollTo(0, 0);
      } else {
        setSubmitError(data.detail || 'Failed to submit your request. Please try again.');
      }
    } catch (err) {
      console.error('[ContactSupport] Submit error:', err);
      setSubmitError('Unable to submit your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigate with scroll to top
  const handleNavigate = (path) => {
    // Use dynamic path for document-related links
    const finalPath = (path.includes('/portal/documents') || path === '/documents')
      ? (userPolicyNumber ? `/documents/${userPolicyNumber}` : '/portal')
      : path;
    navigate(finalPath);
    window.scrollTo(0, 0);
  };

  // Get current suggestion
  const currentSuggestion = formData.inquiryType && inquirySuggestions[
    inquiryTypes.find(t => t.value === formData.inquiryType)?.suggestion
  ];

  // Success State
  if (submitSuccess) {
    return (
      <PortalLayout pageTitle="Contact Support" data-testid="contact-support-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-0 pt-4 sm:pt-0">
          <Card className="border-0 shadow-xl rounded-xl sm:rounded-2xl overflow-hidden">
            <div className="bg-green-500 p-6 text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Request Submitted</h2>
              <p className="text-green-100">We've received your support request</p>
            </div>
            <CardContent className="p-6">
              {ticketNumber && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-center">
                  <p className="text-sm text-slate-500 mb-1">Your Ticket Number</p>
                  <p className="text-2xl font-bold text-slate-900 font-mono">{ticketNumber}</p>
                </div>
              )}
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Response Time</p>
                    <p className="text-sm text-slate-600">We typically respond within 1-2 business days</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Confirmation Sent</p>
                    <p className="text-sm text-slate-600">Check your email for a copy of your request</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleNavigate('/portal')}
                  className="flex-1 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl"
                >
                  Back to Dashboard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleNavigate('/help')}
                  className="flex-1 rounded-xl"
                >
                  Browse Help Center
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout pageTitle="Contact Support" data-testid="contact-support-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-0 pt-4 sm:pt-0">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 bg-[#3B5BDB]/10 text-[#3B5BDB] px-4 py-1.5 rounded-full text-sm font-medium mb-3 sm:mb-4">
            <MessageSquare className="h-4 w-4" />
            Support
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2 sm:mb-3 px-2 sm:px-0">
            How Can We Help You?
          </h1>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto px-2 sm:px-0">
            Get answers fast or connect with support if you need assistance with your policy, billing, or claims.
          </p>
        </div>

        {/* Quick Actions Section */}
        <div className="mb-8 sm:mb-10">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {quickActions.map((action) => {
              const colors = colorMap[action.color];
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => handleNavigate(action.path)}
                  className={`p-4 rounded-xl border-2 ${colors.border} ${colors.bg} ${colors.hoverBg} transition-all text-left group hover:shadow-md`}
                  data-testid={`quick-action-${action.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${colors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 group-hover:text-[#3B5BDB] transition-colors flex items-center gap-1">
                        {action.title}
                        {action.priority && (
                          <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-600 mt-0.5">{action.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-[#3B5BDB] transition-colors flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Smart Routing Prompt */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-6 sm:mb-8 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900 text-sm sm:text-base">Before contacting support</p>
            <p className="text-xs sm:text-sm text-amber-700 mt-1">
              Most questions can be resolved instantly using the Quick Actions above. If you still need help, submit a request below and our team will assist you.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <Card className="shadow-xl border-0 rounded-xl sm:rounded-2xl overflow-hidden">
          <div className="bg-slate-50 px-4 sm:px-6 py-3 sm:py-4 border-b">
            <h2 className="text-sm sm:text-base font-semibold text-slate-900 flex items-center gap-2">
              <MessageCircle className="h-4 sm:h-5 w-4 sm:w-5 text-[#3B5BDB]" />
              Submit a Support Request
            </h2>
          </div>
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row 1: Name and Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <User className="h-4 w-4 inline mr-1.5 text-slate-400" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] outline-none transition-colors"
                    placeholder="Your full name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <Mail className="h-4 w-4 inline mr-1.5 text-slate-400" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] outline-none transition-colors"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              {/* Row 2: Policy Number and Inquiry Type */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <Hash className="h-4 w-4 inline mr-1.5 text-slate-400" />
                    Policy Number
                  </label>
                  <input
                    type="text"
                    value={formData.policyNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, policyNumber: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] outline-none transition-colors bg-slate-50"
                    placeholder="DVP-XXXXXX-XXXXXXXX"
                    readOnly={!!formData.policyNumber}
                    data-testid="contact-policy-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    <Tag className="h-4 w-4 inline mr-1.5 text-slate-400" />
                    Inquiry Type
                  </label>
                  <select
                    value={formData.inquiryType}
                    onChange={(e) => handleInquiryChange(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] outline-none transition-colors appearance-none bg-white"
                    data-testid="contact-inquiry-select"
                  >
                    {inquiryTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Conditional Suggestion Banner */}
              {showSuggestion && currentSuggestion && !dismissedSuggestion && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-900">{currentSuggestion.title}</p>
                        <p className="text-sm text-blue-700 mt-1">{currentSuggestion.message}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {currentSuggestion.actions.map((action, idx) => (
                            <Button
                              key={idx}
                              type="button"
                              variant={action.primary ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleNavigate(action.path)}
                              className={action.primary ? "bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-lg" : "rounded-lg"}
                            >
                              {action.label}
                              <ArrowRight className="h-3.5 w-3.5 ml-1" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setDismissedSuggestion(true);
                        setShowSuggestion(false);
                      }}
                      className="text-blue-400 hover:text-blue-600 text-sm"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {/* Related Articles Section */}
              {formData.inquiryType && relatedArticles[formData.inquiryType] && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="h-4 w-4 text-[#3B5BDB]" />
                    <p className="font-medium text-slate-900 text-sm">Related Help Articles</p>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    These articles might answer your question:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {relatedArticles[formData.inquiryType].map((article) => (
                      <button
                        key={article.id}
                        type="button"
                        onClick={() => handleNavigate(`/help?category=${article.category}&q=${article.id}`)}
                        className="flex items-center gap-2 p-2.5 bg-white border border-slate-200 rounded-lg hover:border-[#3B5BDB] hover:bg-blue-50 transition-all text-left group"
                        data-testid={`related-article-${article.id}`}
                      >
                        <HelpCircle className="h-4 w-4 text-slate-400 group-hover:text-[#3B5BDB] flex-shrink-0" />
                        <span className="text-sm text-slate-700 group-hover:text-[#3B5BDB] line-clamp-1">
                          {article.question}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-[#3B5BDB] ml-auto flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] outline-none transition-colors"
                  placeholder="Brief summary of your inquiry"
                  data-testid="contact-subject-input"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Message
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] outline-none transition-colors resize-none"
                  placeholder="Please describe your issue or question in detail..."
                  data-testid="contact-message-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  For fastest resolution, include as much detail as possible.
                </p>
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700">{submitError}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || !formData.inquiryType || !formData.subject || !formData.message}
                className="w-full bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl h-12 text-base font-semibold disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Response Expectations */}
        <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 flex items-start gap-3">
            <Clock className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900 text-sm sm:text-base">Response Time</p>
              <p className="text-xs sm:text-sm text-slate-600">Support requests are typically reviewed within 1–2 business days.</p>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 flex items-start gap-3">
            <Phone className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900 text-sm sm:text-base">Need Urgent Help?</p>
              <p className="text-xs sm:text-sm text-slate-600">Call us at <a href="tel:18335677729" className="text-[#3B5BDB] font-medium">1-833-LOSSPAY</a></p>
            </div>
          </div>
        </div>

        {/* Compliance Disclaimer */}
        <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-slate-500">
              <p className="font-medium text-slate-600 mb-1">Disclaimer</p>
              <p>
                This form is for general support inquiries only. Coverage and claims are subject to policy terms, conditions, and eligibility. 
                Submitting a request does not guarantee any specific outcome or coverage determination.
              </p>
            </div>
          </div>
        </div>

        {/* Complaint Guidance Section */}
        <div className="mt-6 sm:mt-8 p-4 sm:p-5 bg-orange-50 rounded-xl border border-orange-200" data-testid="complaint-guidance-section">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-orange-900 mb-2">Not Satisfied? You Have Options.</p>
              <p className="text-sm text-orange-800 mb-3">
                If you are not satisfied with our response, you have the right to file a formal complaint with LossPay or your state insurance department. 
                Filing a complaint will <strong>not</strong> affect your policy or pending claims.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={() => navigate('/help?category=complaints&q=how-to-file-complaint')}
                  className="inline-flex items-center justify-center px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg text-sm font-medium transition-colors"
                  data-testid="view-complaint-procedure-btn"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  How to File a Complaint
                </button>
                <button
                  onClick={() => navigate('/complaint-notice')}
                  className="inline-flex items-center justify-center px-4 py-2 bg-white hover:bg-orange-50 text-orange-700 border border-orange-300 rounded-lg text-sm font-medium transition-colors"
                  data-testid="view-complaint-notice-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Your Complaint Notice
                </button>
              </div>
              <p className="text-xs text-orange-700 mt-3">
                <strong>Direct Contact:</strong> complaints@losspay.com | 1-833-LOSSPAY
              </p>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
